/**
 * GapsFilterDrawer Component
 * 
 * Filter drawer for GAP Tracker (EX6)
 * Uses RightContentDrawer as base component
 * Uses search mode pattern for cycles and processes (like FinancialStatementItemDrawer)
 */

import React, { useState, useEffect } from 'react';
import { Button, DatePicker, Input, Select } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import { SearchOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../components/common/ButtonGroup';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import { GapStatus, GapType } from '../../../services/gapService';
import { RiskRanking } from '../../../services/types';
import { searchCycles, searchProcesses, searchProcessOwners, CycleItem, ProcessItem, ProcessOwner } from '../../../services/hierarchyService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

// ========== TYPE DEFINITIONS ==========

export type ManagementPrioritization = 'High' | 'Med' | 'Low' | 'Unranked';

export type QuarterValue = '0' | '1' | '2' | '3' | '4';

export interface GapsFilterState {
  status?: GapStatus[];
  cycles?: CycleItem[];
  processes?: ProcessItem[];
  gapRiskRanking?: RiskRanking[];
  gapType?: GapType[];
  processOwners?: ProcessOwner[];
  managementPrioritization?: ManagementPrioritization[];
  dateIdentified?: string | null;
  gapDateQuarter?: QuarterValue[] | null;
  gapClosureDateQuarter?: QuarterValue[] | null;
}

export interface GapsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: GapsFilterState) => void;
  currentFilters: GapsFilterState;
  annualReportId: string | null;
}

// ========== STYLED COMPONENTS ==========

const FilterSection = styled.div`
  margin-bottom: 24px;
`;

const FilterLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: #1F2937;
  margin-bottom: 8px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    border-radius: 8px !important;
    min-height: 40px !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  border-radius: 8px !important;
`;

// Custom Select Tag for styled display
const SelectTag = styled.span<{ $backgroundColor: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  color: ${props => props.$color};
  margin: 2px;
`;

const SelectTagCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

// Search mode styled components
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    height: 44px;
    border-radius: 6px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;

    .ant-input {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    .anticon-search {
      color: #6B7280;
    }
  }
`;

const SearchResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SearchResultItem = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }

  .item-label {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;

  &:hover {
    border-color: #2563EB;
  }
`;

const TagContainerDisabled = styled(TagContainer)`
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  
  &:hover {
    border-color: #D1D5DB;
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const Tag = styled.span<{ 
  $backgroundColor: string; 
  $color: string;
  $isMoreTag?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  background: ${props => props.$backgroundColor};
  color: ${props => props.$color};
  cursor: ${props => props.$isMoreTag ? 'pointer' : 'default'};
  white-space: nowrap;
`;

const StyledCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

const PlaceholderText = styled.span`
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const DisabledHint = styled.div`
  font-size: 12px;
  color: #9CA3AF;
  margin-top: 4px;
`;

// ========== CONSTANTS ==========

const STATUS_OPTIONS: { value: GapStatus; label: string }[] = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Open', label: 'Open' },
  { value: 'Archived', label: 'Archived' },
];

const RISK_RANKING_OPTIONS: { value: RiskRanking; label: string }[] = [
  { value: 'High', label: 'High' },
  { value: 'Med', label: 'Medium' },
  { value: 'Low', label: 'Low' },
  { value: 'Unranked', label: 'Unranked' },
];

const GAP_TYPE_OPTIONS: { value: GapType; label: string }[] = [
  { value: 'Design', label: 'Design' },
  { value: 'Effectiveness', label: 'Effectiveness' },
  { value: 'Other', label: 'Other' },
];

const MANAGEMENT_PRIORITIZATION_OPTIONS: { value: ManagementPrioritization; label: string }[] = [
  { value: 'High', label: 'High' },
  { value: 'Med', label: 'Medium' },
  { value: 'Low', label: 'Low' },
  { value: 'Unranked', label: 'Unranked' },
];

// ========== HELPER FUNCTIONS ==========

const QUARTER_OPTIONS: { value: QuarterValue; label: string }[] = [
  { value: '0', label: 'Q0' },
  { value: '1', label: 'Q1' },
  { value: '2', label: 'Q2' },
  { value: '3', label: 'Q3' },
  { value: '4', label: 'Q4' },
];

const getActiveFilterCount = (filters: GapsFilterState): number => {
  let count = 0;
  if (filters.status?.length) count++;
  if (filters.cycles?.length) count++;
  if (filters.processes?.length) count++;
  if (filters.gapRiskRanking?.length) count++;
  if (filters.gapType?.length) count++;
  if (filters.processOwners?.length) count++;
  if (filters.managementPrioritization?.length) count++;
  if (filters.dateIdentified) count++;
  if (filters.gapDateQuarter || filters.gapClosureDateQuarter) count++;
  return count;
};

const getEmptyFilters = (): GapsFilterState => ({
  status: undefined,
  cycles: undefined,
  processes: undefined,
  gapRiskRanking: undefined,
  gapType: undefined,
  processOwners: undefined,
  managementPrioritization: undefined,
  dateIdentified: null,
  gapDateQuarter: null,
  gapClosureDateQuarter: null,
});

// ========== MAIN COMPONENT ==========

const GapsFilterDrawer: React.FC<GapsFilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  currentFilters,
  annualReportId,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [localFilters, setLocalFilters] = useState<GapsFilterState>(currentFilters);
  
  // Search mode states - only for cycle, process, processOwner
  type SearchType = 'cycle' | 'process' | 'processOwner';
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>('cycle');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Search results
  const [cycleResults, setCycleResults] = useState<CycleItem[]>([]);
  const [processResults, setProcessResults] = useState<ProcessItem[]>([]);
  const [processOwnerResults, setProcessOwnerResults] = useState<ProcessOwner[]>([]);
  
  // Temp selections (for search mode)
  const [tempSelectedCycles, setTempSelectedCycles] = useState<CycleItem[]>([]);
  const [tempSelectedProcesses, setTempSelectedProcesses] = useState<ProcessItem[]>([]);
  const [tempSelectedProcessOwners, setTempSelectedProcessOwners] = useState<ProcessOwner[]>([]);

  // Helper function to get status colors
  const getStatusColors = (status: GapStatus) => {
    switch (status) {
      case 'Open': return { bg: '#CCFBF1', text: '#0D9488', closeBg: '#99F6E4' };
      case 'Draft': return { bg: '#FEF3C7', text: '#B45309', closeBg: '#FDE68A' };
      case 'Archived': return { bg: '#F3F4F6', text: '#6B7280', closeBg: '#E5E7EB' };
      default: return { bg: '#F3F4F6', text: '#374151', closeBg: '#E5E7EB' };
    }
  };

  // Helper function to get risk ranking colors
  const getRiskColors = (risk: RiskRanking) => {
    switch (risk) {
      case 'High': return { bg: '#FEE2E2', text: '#EF4444', closeBg: '#FECACA' };
      case 'Med': return { bg: '#FEF3C7', text: '#B45309', closeBg: '#FDE68A' };
      case 'Low': return { bg: '#D1FAE5', text: '#059669', closeBg: '#A7F3D0' };
      default: return { bg: '#DBEAFE', text: '#2563EB', closeBg: '#BFDBFE' };
    }
  };

  // Custom tag render for Status Select
  const renderStatusTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const colors = getStatusColors(value as GapStatus);
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Risk Ranking Select
  const renderRiskTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const colors = getRiskColors(value as RiskRanking);
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for GAP Type Select
  const renderTypeTag = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor="#DBEAFE" $color="#1D4ED8" onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill="#BFDBFE" $strokeColor="#1D4ED8" onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Custom tag render for Management Prioritization Select (same colors as Risk)
  const renderPrioTag = (props: CustomTagProps) => {
    const { label, value, closable, onClose } = props;
    const colors = getRiskColors(value as RiskRanking);
    const onPreventMouseDown = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
    return (
      <SelectTag $backgroundColor={colors.bg} $color={colors.text} onMouseDown={onPreventMouseDown}>
        {label}
        {closable && <SelectTagCloseIcon $circleFill={colors.closeBg} $strokeColor={colors.text} onClick={(e) => { e.stopPropagation(); onClose(); }} />}
      </SelectTag>
    );
  };

  // Sync local filters when drawer opens or current filters change
  useEffect(() => {
    if (open) {
      setLocalFilters(currentFilters);
      setIsSearchMode(false);
    }
  }, [open, currentFilters]);

  // Search mode handlers - only for cycle, process, processOwner
  const enterSearchMode = (type: SearchType) => {
    setSearchType(type);
    setIsSearchMode(true);
    setSearchQuery('');
    
    // Load existing selections into temp states
    setTempSelectedCycles([...(localFilters.cycles || [])]);
    setTempSelectedProcesses([...(localFilters.processes || [])]);
    setTempSelectedProcessOwners([...(localFilters.processOwners || [])]);
    
    // Clear previous results
    setCycleResults([]);
    setProcessResults([]);
    setProcessOwnerResults([]);
  };

  const exitSearchMode = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setTempSelectedCycles([]);
    setTempSelectedProcesses([]);
    setTempSelectedProcessOwners([]);
    setCycleResults([]);
    setProcessResults([]);
    setProcessOwnerResults([]);
  };

  // Fetch search results when query changes - only for cycle, process, processOwner
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!isSearchMode) return;
      
      // processOwner search needs companyId, cycle/process search needs annualReportId
      if (searchType === 'processOwner' && !user?.companyId) return;
      if (searchType !== 'processOwner' && !annualReportId) return;
      
      setIsSearching(true);
      
      try {
        if (searchType === 'cycle') {
          const results = await searchCycles({
            annualReportId: annualReportId!,
            searchTerm: searchQuery,
          });
          setCycleResults(results);
        } else if (searchType === 'process') {
          // Extract cycleIds from selected cycles
          const cycleIds = tempSelectedCycles.map(cycle => cycle.cycleId);
          const results = await searchProcesses({
            annualReportId: annualReportId!,
            searchTerm: searchQuery,
            cycleIds,
          });
          setProcessResults(results);
        } else if (searchType === 'processOwner') {
          const results = await searchProcessOwners({
            companyId: user!.companyId,
            searchTerm: searchQuery,
          });
          setProcessOwnerResults(results);
        }
      } catch (error) {
        console.error('Search failed:', error);
        if (searchType === 'cycle') {
          setCycleResults([]);
        } else if (searchType === 'process') {
          setProcessResults([]);
        } else {
          setProcessOwnerResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, isSearchMode, annualReportId, tempSelectedCycles, user?.companyId]);

  const handleSearchResultClick = (value: CycleItem | ProcessItem | ProcessOwner) => {
    if (searchType === 'cycle') {
      const cycleValue = value as CycleItem;
      const exists = tempSelectedCycles.some(c => c.cycleId === cycleValue.cycleId);
      if (exists) {
        setTempSelectedCycles(tempSelectedCycles.filter(v => v.cycleId !== cycleValue.cycleId));
      } else {
        setTempSelectedCycles([...tempSelectedCycles, cycleValue]);
      }
    } else if (searchType === 'process') {
      const processValue = value as ProcessItem;
      const exists = tempSelectedProcesses.some(p => p.processId === processValue.processId);
      if (exists) {
        setTempSelectedProcesses(tempSelectedProcesses.filter(v => v.processId !== processValue.processId));
      } else {
        setTempSelectedProcesses([...tempSelectedProcesses, processValue]);
      }
    } else if (searchType === 'processOwner') {
      const ownerValue = value as ProcessOwner;
      const exists = tempSelectedProcessOwners.some(o => o.userId === ownerValue.userId);
      if (exists) {
        setTempSelectedProcessOwners(tempSelectedProcessOwners.filter(v => v.userId !== ownerValue.userId));
      } else {
        setTempSelectedProcessOwners([...tempSelectedProcessOwners, ownerValue]);
      }
    }
  };

  const applySearchSelection = () => {
    if (searchType === 'cycle') {
      setLocalFilters(prev => ({
        ...prev,
        cycles: tempSelectedCycles,
        processes: [], // Clear processes when cycles change
      }));
    } else if (searchType === 'process') {
      setLocalFilters(prev => ({
        ...prev,
        processes: tempSelectedProcesses,
      }));
    } else if (searchType === 'processOwner') {
      setLocalFilters(prev => ({
        ...prev,
        processOwners: tempSelectedProcessOwners,
      }));
    }
    
    exitSearchMode();
  };

  const handleRemoveCycle = (cycleId: string) => {
    setLocalFilters(prev => ({
      ...prev,
      cycles: (prev.cycles || []).filter(c => c.cycleId !== cycleId),
      // Clear processes when cycles change
      processes: [],
    }));
  };

  const handleRemoveProcess = (processId: string) => {
    setLocalFilters(prev => ({
      ...prev,
      processes: (prev.processes || []).filter(p => p.processId !== processId),
    }));
  };

  const handleClearAll = () => {
    const emptyFilters = getEmptyFilters();
    setLocalFilters(emptyFilters);
    onApply(emptyFilters);
    onClose();
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleDrawerClose = () => {
    if (isSearchMode) {
      exitSearchMode();
    } else {
      onClose();
    }
  };

  const activeCount = getActiveFilterCount(localFilters);
  const selectedCycles = localFilters.cycles || [];
  const selectedProcesses = localFilters.processes || [];
  const canSelectProcess = selectedCycles.length > 0;

  // Render search UI - only for cycle, process, processOwner
  const renderSearchUI = () => {
    const getSearchConfig = () => {
      switch (searchType) {
        case 'cycle':
          return {
            results: cycleResults,
            placeholder: 'Search Cycle...',
            emptyLabel: 'cycles',
            getItemId: (item: CycleItem) => item.cycleId,
            getItemLabel: (item: CycleItem) => `${item.cycleCode} - ${item.cycleName}`,
            isSelected: (item: CycleItem) => tempSelectedCycles.some(c => c.cycleId === item.cycleId),
          };
        case 'process':
          return {
            results: processResults,
            placeholder: 'Search Process...',
            emptyLabel: 'processes',
            getItemId: (item: ProcessItem) => item.processId,
            getItemLabel: (item: ProcessItem) => `${item.processCode} - ${item.processName}`,
            isSelected: (item: ProcessItem) => tempSelectedProcesses.some(p => p.processId === item.processId),
          };
        case 'processOwner':
          return {
            results: processOwnerResults,
            placeholder: 'Search Process Owner...',
            emptyLabel: 'process owners',
            getItemId: (item: ProcessOwner) => item.userId,
            getItemLabel: (item: ProcessOwner) => item.userName + (item.title ? ` (${item.title})` : ''),
            isSelected: (item: ProcessOwner) => tempSelectedProcessOwners.some(o => o.userId === item.userId),
          };
      }
    };

    const config = getSearchConfig();

    return (
      <SearchContainer>
        <SearchInputWrapper>
          <Input
            prefix={<SearchOutlined />}
            suffix={
              isSearching && searchQuery !== '' ? (
                <LoadingOutlined style={{ color: '#2563EB' }} />
              ) : null
            }
            placeholder={config.placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </SearchInputWrapper>

        <SearchResultsList>
          {config.results.length === 0 ? (
            searchQuery === '' ? (
              <EmptyState>{isSearching ? 'Loading...' : `There are no ${config.emptyLabel} to display`}</EmptyState>
            ) : (
              <EmptyState>{isSearching ? 'Searching...' : 'There are no results found'}</EmptyState>
            )
          ): (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            config.results.map((item: any) => {
              const itemId = config.getItemId(item);
              const itemLabel = config.getItemLabel(item);
              const isSelected = config.isSelected(item);
              return (
                <SearchResultItem
                  key={itemId}
                  $selected={isSelected}
                  onClick={() => handleSearchResultClick(item)}
                >
                  <div className="item-label">{itemLabel}</div>
                </SearchResultItem>
              );
            })
          )}
        </SearchResultsList>
      </SearchContainer>
    );
  };

  // Render filter UI
  const renderFilterUI = () => (
    <>
      {/* Status Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Status</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select status"
          value={localFilters.status || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, status: value as GapStatus[] }))}
          options={STATUS_OPTIONS}
          tagRender={renderStatusTag}
          allowClear
        />
      </FilterSection>

      {/* Cycle Filter - Search Mode */}
      <FilterSection>
        <FilterLabel>Cycle</FilterLabel>
        <TagContainer onClick={() => enterSearchMode('cycle')}>
          {selectedCycles.length === 0 ? (
            <PlaceholderText>Select cycles...</PlaceholderText>
          ) : (
            selectedCycles.map(cycle => (
              <Tag 
                key={cycle.cycleId} 
                $backgroundColor="#E0E7FF" 
                $color="#4338CA"
              >
                {cycle.cycleCode} - {cycle.cycleName}
                <StyledCloseIcon
                  $circleFill="#C7D2FE"
                  $strokeColor="#4338CA"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveCycle(cycle.cycleId);
                  }}
                />
              </Tag>
            ))
          )}
          <ArrowButton><RightOutlined /></ArrowButton>
        </TagContainer>
      </FilterSection>

      {/* Process Filter - Search Mode (only enabled when cycle is selected) */}
      <FilterSection>
        <FilterLabel>Process</FilterLabel>
        {canSelectProcess ? (
          <TagContainer onClick={() => enterSearchMode('process')}>
            {selectedProcesses.length === 0 ? (
              <PlaceholderText>Select processes...</PlaceholderText>
            ) : (
              selectedProcesses.map(process => (
                <Tag 
                  key={process.processId} 
                  $backgroundColor="#DCFCE7" 
                  $color="#166534"
                >
                  {process.processCode} - {process.processName}
                  <StyledCloseIcon
                    $circleFill="#BBF7D0"
                    $strokeColor="#166534"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveProcess(process.processId);
                    }}
                  />
                </Tag>
              ))
            )}
            <ArrowButton><RightOutlined /></ArrowButton>
          </TagContainer>
        ) : (
          <>
            <TagContainerDisabled>
              <PlaceholderText>Select cycles first...</PlaceholderText>
              <ArrowButton><RightOutlined /></ArrowButton>
            </TagContainerDisabled>
            <DisabledHint>Please select at least one cycle first</DisabledHint>
          </>
        )}
      </FilterSection>

      {/* Process Owner Filter - Search Mode */}
      <FilterSection>
        <FilterLabel>Process Owner</FilterLabel>
        <TagContainer onClick={() => enterSearchMode('processOwner')}>
          {(localFilters.processOwners || []).length === 0 ? (
            <PlaceholderText>Search process owner...</PlaceholderText>
          ) : (
            <>
              {(localFilters.processOwners || []).slice(0, 2).map((owner) => (
                <Tag 
                  key={owner.userId} 
                  $backgroundColor="#DBEAFE" 
                  $color="#1D4ED8"
                >
                  {owner.userName}
                  <StyledCloseIcon
                    $circleFill="#93C5FD"
                    $strokeColor="#1D4ED8"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLocalFilters(prev => ({
                        ...prev,
                        processOwners: prev.processOwners?.filter(o => o.userId !== owner.userId),
                      }));
                    }}
                  />
                </Tag>
              ))}
              {(localFilters.processOwners || []).length > 2 && (
                <Tag $backgroundColor="#F3F4F6" $color="#6B7280" $isMoreTag>
                  +{(localFilters.processOwners || []).length - 2}
                </Tag>
              )}
            </>
          )}
          <ArrowButton><RightOutlined /></ArrowButton>
        </TagContainer>
      </FilterSection>

      {/* GAP Risk Ranking Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>GAP Risk Ranking</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select risk ranking"
          value={localFilters.gapRiskRanking?.filter(r => r != 'null') || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, gapRiskRanking: value as RiskRanking[] }))}
          options={RISK_RANKING_OPTIONS}
          tagRender={renderRiskTag}
          allowClear
        />
      </FilterSection>

      {/* GAP Type Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>GAP Type</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select GAP type"
          value={localFilters.gapType || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, gapType: value as GapType[] }))}
          options={GAP_TYPE_OPTIONS}
          tagRender={renderTypeTag}
          allowClear
        />
      </FilterSection>

      {/* Management Prioritization Filter - Dropdown with styled tags */}
      <FilterSection>
        <FilterLabel>Management Prioritization</FilterLabel>
        <StyledSelect
          mode="multiple"
          placeholder="Select prioritization"
          value={localFilters.managementPrioritization || []}
          onChange={(value) => setLocalFilters(prev => ({ ...prev, managementPrioritization: value as ManagementPrioritization[] }))}
          options={MANAGEMENT_PRIORITIZATION_OPTIONS}
          tagRender={renderPrioTag}
          allowClear
        />
      </FilterSection>

      {/* Quarter Filter */}
      <FilterSection>
        <FilterLabel>Quarter</FilterLabel>
        <StyledSelect
          placeholder="Select quarter"
          allowClear
          value={localFilters.gapDateQuarter || localFilters.gapClosureDateQuarter || undefined}
          onChange={(value) => {
            const q = (value as QuarterValue[]) || null;
            setLocalFilters(prev => ({
              ...prev,
              gapDateQuarter: q,
              gapClosureDateQuarter: q,
            }));
          }}
          options={QUARTER_OPTIONS}
        />
      </FilterSection>

      {/* Date Identified Filter - Single date with equal operator */}
      <FilterSection>
        <FilterLabel>Date Identified</FilterLabel>
        <StyledDatePicker
          placeholder="Select date"
          format="YYYY-MM-DD"
          value={localFilters.dateIdentified ? dayjs(localFilters.dateIdentified) : null}
          onChange={(date) => setLocalFilters(prev => ({
            ...prev,
            dateIdentified: date ? (date as Dayjs).format('YYYY-MM-DD') : null,
          }))}
          style={{ width: '100%' }}
        />
      </FilterSection>
    </>
  );

  return (
    <RightContentDrawer
      open={open}
      onClose={handleDrawerClose}
      title={isSearchMode ? `Select ${
        searchType === 'cycle' ? 'Cycles' : 
        searchType === 'process' ? 'Processes' : 
        'Process Owners'
      }` : 'Filter'}
      width={600}
      resizable={true}
      footer={
        isSearchMode ? (
          <ButtonGroup>
            <Button type="default" onClick={exitSearchMode}>
              Go back
            </Button>
            <Button type="primary" onClick={applySearchSelection}>
              Done
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup>
            {activeCount > 0 ? (
              <Button type="default" onClick={handleClearAll}>
                Clear Filter
              </Button>
            ) : (
              <Button type="default" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button type="primary" onClick={handleApply}>
              Apply Filters
            </Button>
          </ButtonGroup>
        )
      }
    >
      {isSearchMode ? renderSearchUI() : renderFilterUI()}
    </RightContentDrawer>
  );
};

export default GapsFilterDrawer;
